/**
 * Question Bank Management Page
 * Admin interface for managing extracted questions
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './QuestionBank.css';
import * as questionBankService from '../services/questionBankService';
import booksService from '../services/booksService';
import QuestionBankValidation from '../components/QuestionBankValidation';

const QuestionBank = () => {
  const [activeTab, setActiveTab] = useState('extraction'); // extraction, review, queue
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  useEffect(() => {
    loadBooks();
    loadQueueStats();
    const interval = setInterval(loadQueueStats, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === 'review') {
      loadReviewQueue();
    }
  }, [activeTab]);

  const loadBooks = async () => {
    try {
      const response = await booksService.getAll();
      // booksService.getAll() returns { data: [], pagination: {} }
      // Filter only books marked for question extraction
      const extractableBooks = (response.data || []).filter(book => book.extract_questions);
      setBooks(extractableBooks);
    } catch (error) {
      console.error('Error loading books:', error);
      toast.error('Failed to load books');
    }
  };

  const loadQueueStats = async () => {
    try {
      const data = await questionBankService.getQueueStatistics();
      setQueueStats(data);
    } catch (error) {
      console.error('Error loading queue stats:', error);
    }
  };

  const loadReviewQueue = async () => {
    try {
      setLoading(true);
      const data = await questionBankService.getReviewQueue(1, 50);
      setReviewQueue(data.questions || []);
    } catch (error) {
      console.error('Error loading review queue:', error);
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  const handleBookSelect = async (bookId) => {
    try {
      const book = books.find(b => b.id === parseInt(bookId));
      setSelectedBook(book);

      if (book) {
        const status = await questionBankService.getExtractionStatus(book.id);
        setExtractionStatus(status);
      }
    } catch (error) {
      console.error('Error loading extraction status:', error);
      toast.error('Failed to load extraction status');
    }
  };

  const handleTriggerExtraction = async () => {
    if (!selectedBook) {
      toast.warning('Please select a book first');
      return;
    }

    try {
      setLoading(true);
      const result = await questionBankService.triggerExtraction(selectedBook.id);
      toast.success(`Extraction started! Job ID: ${result.jobId}`);

      // Reload status after a moment
      setTimeout(() => handleBookSelect(selectedBook.id), 1000);
    } catch (error) {
      console.error('Error triggering extraction:', error);
      toast.error(error.response?.data?.error || 'Failed to start extraction');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryJob = async (jobId) => {
    try {
      await questionBankService.retryJob(jobId);
      toast.success('Job retried successfully');
      loadQueueStats();
    } catch (error) {
      console.error('Error retrying job:', error);
      toast.error('Failed to retry job');
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await questionBankService.cancelJob(jobId);
      toast.success('Job cancelled successfully');
      loadQueueStats();
    } catch (error) {
      console.error('Error cancelling job:', error);
      toast.error('Failed to cancel job');
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion({...question});
  };

  const handleSaveQuestion = async () => {
    if (!editingQuestion) return;

    try {
      await questionBankService.updateQuestion(editingQuestion.id, {
        question_text: editingQuestion.question_text,
        option_a: editingQuestion.option_a,
        option_b: editingQuestion.option_b,
        option_c: editingQuestion.option_c,
        option_d: editingQuestion.option_d,
        option_e: editingQuestion.option_e,
        correct_answer: editingQuestion.correct_answer,
        needs_review: false
      });

      toast.success('Question updated successfully');
      setEditingQuestion(null);
      loadReviewQueue();
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
    }
  };

  const handleMarkAsCorrect = async (questionId) => {
    try {
      await questionBankService.updateQuestion(questionId, {
        needs_review: false
      });
      toast.success('Question marked as correct');
      loadReviewQueue();
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
    }
  };

  const renderExtractionTab = () => (
    <div className="extraction-panel">
      <div className="panel-section">
        <h3>Book Selection</h3>
        {books.length === 0 ? (
          <div className="info-message">
            <p>No books available for question extraction.</p>
            <p>Please upload a book and mark it for question extraction in the Books page first.</p>
          </div>
        ) : (
          <div className="book-selector">
            <select
              onChange={(e) => handleBookSelect(e.target.value)}
              value={selectedBook?.id || ''}
              className="book-dropdown"
            >
              <option value="">Select a book...</option>
              {books.map(book => (
                <option key={book.id} value={book.id}>
                  {book.name}
                </option>
              ))}
            </select>

            <button
              onClick={handleTriggerExtraction}
              disabled={!selectedBook || loading}
              className="btn btn-primary"
            >
              {loading ? 'Starting...' : 'Start Extraction'}
            </button>
          </div>
        )}
      </div>

      {extractionStatus && (
        <div className="panel-section">
          <h3>Extraction Status</h3>
          <div className="status-card">
            <div className="status-row">
              <span className="status-label">Status:</span>
              <span className={`status-value status-${extractionStatus.status}`}>
                {extractionStatus.status}
              </span>
            </div>

            {extractionStatus.current_job && (
              <>
                <div className="status-row">
                  <span className="status-label">Job ID:</span>
                  <span className="status-value">{extractionStatus.current_job.id}</span>
                </div>
                <div className="status-row">
                  <span className="status-label">Progress:</span>
                  <span className="status-value">{extractionStatus.current_job.progress}%</span>
                </div>
                <div className="status-row">
                  <span className="status-label">Started:</span>
                  <span className="status-value">
                    {new Date(extractionStatus.current_job.started_at).toLocaleString()}
                  </span>
                </div>
              </>
            )}

            {extractionStatus.stats && (
              <>
                <div className="status-divider"></div>
                <div className="status-row">
                  <span className="status-label">Questions Extracted:</span>
                  <span className="status-value">{extractionStatus.stats.total_questions || 0}</span>
                </div>
                <div className="status-row">
                  <span className="status-label">Needs Review:</span>
                  <span className="status-value">{extractionStatus.stats.needs_review || 0}</span>
                </div>
                <div className="status-row">
                  <span className="status-label">Last Extraction:</span>
                  <span className="status-value">
                    {extractionStatus.stats.last_extraction
                      ? new Date(extractionStatus.stats.last_extraction).toLocaleString()
                      : 'Never'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderReviewTab = () => (
    <div className="review-panel">
      <div className="panel-header">
        <h3>Questions Needing Review</h3>
        <button onClick={loadReviewQueue} className="btn btn-secondary">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : reviewQueue.length === 0 ? (
        <div className="empty-state">
          <p>No questions need review</p>
        </div>
      ) : (
        <div className="questions-list">
          {reviewQueue.map((question) => (
            <div key={question.id} className="question-card">
              <div className="question-header">
                <div className="question-meta">
                  <span className="book-name">{question.book_name}</span>
                  <span className="question-number">Q.{question.question_number}</span>
                  <span className="page-number">Page {question.page_number}</span>
                  <span className={`confidence confidence-${Math.floor(question.extraction_confidence * 10)}`}>
                    {(question.extraction_confidence * 100).toFixed(0)}% confidence
                  </span>
                </div>
                <div className="question-actions">
                  <button
                    onClick={() => handleEditQuestion(question)}
                    className="btn-icon"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleMarkAsCorrect(question.id)}
                    className="btn-icon"
                    title="Mark as Correct"
                  >
                    ✓
                  </button>
                </div>
              </div>

              <div className="question-content">
                <p className="question-text">{question.question_text}</p>
                <div className="options-grid">
                  <div className="option">
                    <span className="option-label">A)</span>
                    <span className="option-text">{question.option_a}</span>
                  </div>
                  <div className="option">
                    <span className="option-label">B)</span>
                    <span className="option-text">{question.option_b}</span>
                  </div>
                  <div className="option">
                    <span className="option-label">C)</span>
                    <span className="option-text">{question.option_c}</span>
                  </div>
                  <div className="option">
                    <span className="option-label">D)</span>
                    <span className="option-text">{question.option_d}</span>
                  </div>
                  {question.option_e && (
                    <div className="option">
                      <span className="option-label">E)</span>
                      <span className="option-text">{question.option_e}</span>
                    </div>
                  )}
                </div>
                {question.correct_answer && (
                  <div className="correct-answer">
                    <strong>Answer:</strong> {question.correct_answer.toUpperCase()}
                  </div>
                )}
                {!question.has_answer && (
                  <div className="warning-badge">⚠️ No answer found</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingQuestion && (
        <div className="modal-overlay" onClick={() => setEditingQuestion(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Question</h3>
              <button onClick={() => setEditingQuestion(null)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Question Text</label>
                <textarea
                  value={editingQuestion.question_text}
                  onChange={(e) => setEditingQuestion({...editingQuestion, question_text: e.target.value})}
                  rows="3"
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Option A</label>
                <input
                  type="text"
                  value={editingQuestion.option_a}
                  onChange={(e) => setEditingQuestion({...editingQuestion, option_a: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Option B</label>
                <input
                  type="text"
                  value={editingQuestion.option_b}
                  onChange={(e) => setEditingQuestion({...editingQuestion, option_b: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Option C</label>
                <input
                  type="text"
                  value={editingQuestion.option_c}
                  onChange={(e) => setEditingQuestion({...editingQuestion, option_c: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Option D</label>
                <input
                  type="text"
                  value={editingQuestion.option_d}
                  onChange={(e) => setEditingQuestion({...editingQuestion, option_d: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Option E (optional)</label>
                <input
                  type="text"
                  value={editingQuestion.option_e || ''}
                  onChange={(e) => setEditingQuestion({...editingQuestion, option_e: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Correct Answer</label>
                <select
                  value={editingQuestion.correct_answer || ''}
                  onChange={(e) => setEditingQuestion({...editingQuestion, correct_answer: e.target.value})}
                  className="form-control"
                >
                  <option value="">Select...</option>
                  <option value="a">A</option>
                  <option value="b">B</option>
                  <option value="c">C</option>
                  <option value="d">D</option>
                  <option value="e">E</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setEditingQuestion(null)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleSaveQuestion} className="btn btn-primary">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderQueueTab = () => (
    <div className="queue-panel">
      <h3>Queue Statistics</h3>
      {queueStats ? (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Waiting</div>
            <div className="stat-value">{queueStats.waiting || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active</div>
            <div className="stat-value">{queueStats.active || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Completed</div>
            <div className="stat-value stat-success">{queueStats.completed || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Failed</div>
            <div className="stat-value stat-error">{queueStats.failed || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Delayed</div>
            <div className="stat-value">{queueStats.delayed || 0}</div>
          </div>
        </div>
      ) : (
        <div className="loading-spinner">Loading...</div>
      )}

      {queueStats && queueStats.jobs && queueStats.jobs.length > 0 && (
        <div className="jobs-section">
          <h4>Recent Jobs</h4>
          <div className="jobs-list">
            {queueStats.jobs.map((job) => (
              <div key={job.id} className="job-card">
                <div className="job-header">
                  <span className="job-id">Job #{job.id}</span>
                  <span className={`job-status status-${job.state}`}>{job.state}</span>
                </div>
                <div className="job-details">
                  <div>Book ID: {job.data.bookId}</div>
                  <div>Progress: {job.progress}%</div>
                  {job.state === 'failed' && job.failedReason && (
                    <div className="job-error">Error: {job.failedReason}</div>
                  )}
                </div>
                <div className="job-actions">
                  {job.state === 'failed' && (
                    <button onClick={() => handleRetryJob(job.id)} className="btn btn-sm btn-primary">
                      Retry
                    </button>
                  )}
                  {(job.state === 'waiting' || job.state === 'active') && (
                    <button onClick={() => handleCancelJob(job.id)} className="btn btn-sm btn-danger">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="question-bank-page">
      <div className="page-header">
        <h1>Question Bank</h1>
        <p>Extract and manage MCQ questions from medical textbooks</p>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'extraction' ? 'active' : ''}`}
          onClick={() => setActiveTab('extraction')}
        >
          Extraction
        </button>
        <button
          className={`tab ${activeTab === 'review' ? 'active' : ''}`}
          onClick={() => setActiveTab('review')}
        >
          Review Queue
          {reviewQueue.length > 0 && (
            <span className="badge">{reviewQueue.length}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'validation' ? 'active' : ''}`}
          onClick={() => setActiveTab('validation')}
        >
          Validation & Explanations
        </button>
        <button
          className={`tab ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => setActiveTab('queue')}
        >
          Queue Status
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'extraction' && renderExtractionTab()}
        {activeTab === 'review' && renderReviewTab()}
        {activeTab === 'validation' && <QuestionBankValidation />}
        {activeTab === 'queue' && renderQueueTab()}
      </div>
    </div>
  );
};

export default QuestionBank;
